import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom'
import Chart from 'chart.js';
import { DateTime } from 'luxon';
import { fade } from '@material-ui/core/styles/colorManipulator';

import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';

import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh'

import Loading from 'app/components/loaders/Loading';

import { paper, divider, adminPageTitle } from 'app/styles/core';
import { containerFlex, containerFlexFixed, containerFlexGrow } from 'app/styles/new-layout';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';
import AccessRequests from 'app/components/accessrequests/AccessRequests';
import ResignationRequests from 'app/components/resignationrequests/ResignationRequests';


const useTaskStyles = makeStyles(theme => ({
    tasksContainer: {
        position: 'relative'
    },
    taskContainer: {
        padding: theme.spacing(2),
        borderBottom: '1px solid ' + theme.palette.divider
    },
    taskLink: {
        display: 'block',
        marginBottom: theme.spacing(2)
    },
    tabs: {
        borderBottom: `1px solid ${theme.palette.divider}`
    },
}));

function Tasks(props) {

    const [ pendingTasks, setPendingTasks ] = useState([]);
    const [ completedTasks, setCompletedTasks ] = useState([]);
    const [ tasksLoading, setTasksLoading ] = useState(false);
    const [ taskTab, setTaskTab ] = useState(0);

    const classes = useTaskStyles();
    const api = useApi();

    const getTasks = () => {
        setTasksLoading(true);

        api.get('/admin/tasks')
            .then(res => {
                const newPendingTasks = res.tasks.pending;
                const newCompletedTasks = res.tasks.completed;

                setPendingTasks(newPendingTasks);
                setCompletedTasks(newCompletedTasks);

            }).catch(error => {
                console.log(error.response);
            }).then(setTasksLoading(false));
    }

    useEffect(() => {
        getTasks();
    }, []);

    const handleTasksTabChange = (event, newTaskTab) => {
        setTaskTab(newTaskTab);
    };

    const postTask = (id, action) => {
        setTasksLoading(true);

        api.post('/admin/tasks/' + action, {'id': id})
            .then(res => {
                const newPendingTasks = res.tasks.pending;
                const newCompletedTasks = res.tasks.completed;

                setPendingTasks(newPendingTasks);
                setCompletedTasks(newCompletedTasks);

                if (action === 'pending' && newCompletedTasks.length === 0) {
                    setTaskTab(0);
                }

            }).catch(error => {
                //console.log(error.response);
            }).then(() => {
                setTasksLoading(false)
            });
    }

    return (
        <Fragment>
            <Paper square>
                <Tabs
                    value={taskTab}
                    indicatorColor="secondary"
                    textColor="secondary"
                    onChange={handleTasksTabChange}
                    aria-label="Tasks"
                    className={classes.tabs}
                    >
                    <Tab label="Pending" disabled={tasksLoading}/>
                    <Tab label="Completed" disabled={tasksLoading}/>
                </Tabs>
            </Paper>
            {taskTab === 0 ? (
                <div className={classes.tasksContainer}>
                    <Loading isLoading={tasksLoading}/>
                    {pendingTasks.length ? pendingTasks.map(task => (
                        <Paper square key={task.id} className={classes.taskContainer}>
                            <Typography variant="subtitle2" gutterBottom>{task.data.title}</Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                gutterBottom
                                >
                                {task.data.message}
                            </Typography>
                            <Link
                                variant="subtitle2"
                                color="secondary"
                                className={classes.taskLink}
                                component={RouterLink}
                                to={task.data.link_url}
                                >
                                {task.data.link_text}
                            </Link>
                            <Button
                                size="small"
                                startIcon={<CheckIcon />}
                                color="secondary"
                                onClick={() => postTask(task.id, 'complete')}
                                >
                                Mark as Complete
                            </Button>
                        </Paper>
                    )) : (
                        <Paper square className={classes.taskContainer}>
                            <Typography variant="subtitle1" color="textSecondary">
                                No pending tasks.
                            </Typography>
                        </Paper>
                    )}
                </div>
            ) : (
                <div className={classes.tasksContainer}>
                    <Loading isLoading={tasksLoading}/>
                    {completedTasks.length ? completedTasks.map(task => (
                        <Paper square key={task.id} className={classes.taskContainer}>
                            <Typography variant="subtitle2" gutterBottom>{task.data.title}</Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                className={classes.taskBody}
                                gutterBottom
                                >
                                {task.data.message}
                            </Typography>
                            <Link
                                variant="subtitle2"
                                color="secondary"
                                className={classes.taskLink}
                                component={RouterLink}
                                to={task.data.link_url}
                                >
                                {task.data.link_text}
                            </Link>
                            <Button
                                size="small"
                                startIcon={<CloseIcon />}
                                color="secondary"
                                onClick={() => postTask(task.id, 'pending')}
                                >
                                Mark as Pending
                            </Button>
                        </Paper>
                    )) : (
                        <Paper square className={classes.taskContainer}>
                            <Typography variant="subtitle1" color="textSecondary">
                                No completed tasks.
                            </Typography>
                        </Paper>
                    )}
                </div>
            )}
        </Fragment>
    )
}


const useStyles = makeStyles(theme => ({
    container: {
        ...containerFlex(theme),
        padding: theme.spacing(3),
    },
    content: {
        ...containerFlexGrow(theme),
        position: 'relative',
        paddingRight: theme.spacing(3),

        [theme.breakpoints.down('md')]: {
            paddingRight: '0px',
        }
    },
    nav: {
        ...containerFlexFixed(theme),
    },
    paper: {
        ...paper(theme)
    },
    divider: {
        ...divider(theme)
    },
    pageTitle: {
        ...adminPageTitle
    },
    analyticsToolbar: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: theme.spacing(2),
        justifyContent: 'space-between'
    },
    canvasContainer: {
        position: 'relative',
        width: '100%',
    },
    analyticsSelect: {
        marginLeft: theme.spacing(2)
    },
    rotate: {
        animation: 'spin 1s infinite linear'
    },
    noRotate: {},
    accessRequestsContainer: {
        marginTop: theme.spacing(3)
    }
}));

const timeframes = [
    'Last 30 Days',
    'Last 60 Days',
    'Last 90 Days',
    'Last 180 Days',
    'Current Year',
    'Past 5 Years',
    'All Time'
]

const metrics = [
    {
        value: 'total_users',
        label: 'Total Users'
    },
    {
        value: 'total_members',
        label: 'Total Members'
    },
    {
        value: 'total_guests',
        label: 'Total Guests'
    },
    {
        value: 'users_logged_in',
        label: 'Users Logged In'
    },
    {
        value: 'total_actions',
        label: 'Total User Actions'
    },
    {
        value: 'total_page_views',
        label: 'Total User Page Views'
    },
    {
        value: 'invoice_paid_amount',
        label: 'Total Paid Invoices'
    }
]

const initialConfig = {
    type: 'line',
    data: {
        labels: [],
        datasets: [{
            data: [],

            borderWidth: 1
        }]
    },
    options: {
        legend: {
            display: false
        },
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    stepSize: 1
                }
            }],
            xAxes: [{
                ticks: {
                    maxRotation: 0,
                    autoSkipPadding: 4
                }
            }]
        }
    }
}

let ctx = null;
let analyticsChart = null;

export default function Dashboard(props) {

    const [ analytics, setAnalytics ] = useState([]);
    const [ hourlyAnalytics, setHourlyAnalytics ] = useState({});
    const [ analyticsLoading, setAnalyticsLoading ] = useState(false);
    const [ analyticsTimeFrame, setAnalyticsTimeFrame ] = useState('Last 30 Days');
    const [ analyticsMetric, setAnalyticsMetric ] = useState('users_logged_in');

    const classes = useStyles();
    let theme = useTheme();
    const { app } = useApp();
    const api = useApi();

    // Getter Functions ------------------------------------------

    const getHourlyAnalytics = () => {
        setAnalyticsLoading(true);

        api.get('/admin/analytics')
            .then(res => {
                const newAnalytics = res.analytics;
                setHourlyAnalytics(newAnalytics)})
            .catch(setAnalyticsLoading(false))


    }

    const getAnalytics = () => {

        let dailyAnalytics = [];

        for (const [key, value] of Object.entries(hourlyAnalytics)) {

            let day = key.split(' ')[0]

            if (dailyAnalytics[day]==null) {
                
                dailyAnalytics[day] = {
                    day: day,
                    club_id: value.club_id,
                    invoice_paid_amount: value.invoice_paid_amount,
                    total_actions: value.total_actions,
                    total_guests: value.total_guests,
                    total_members: value.total_members,
                    total_page_views: value.total_page_views,
                    total_users: value.total_users,
                    users_logged_in: value.users_logged_in
                };

            } else {

                if (value.total_guests > dailyAnalytics[day].total_guests) {
                    dailyAnalytics[day].total_guests = value.total_guests
                }
                if (value.total_members > dailyAnalytics[day].total_members) {
                    dailyAnalytics[day].total_members = value.total_members
                }
                if (value.total_users > dailyAnalytics[day].total_users) {
                    dailyAnalytics[day].total_users = value.total_users
                }

                dailyAnalytics[day].invoice_paid_amount += value.invoice_paid_amount 
                  
                dailyAnalytics[day].total_actions += value.total_actions
                
                dailyAnalytics[day].total_page_views += value.total_page_views
                
                dailyAnalytics[day].users_logged_in += value.users_logged_in
            }
        }

        setAnalytics(dailyAnalytics)

        setAnalyticsLoading(false);

    }

    useEffect(() => {
        ctx = document.getElementById('analyticsGraph').getContext("2d");
        analyticsChart = new Chart(ctx, initialConfig)

        getHourlyAnalytics();
    }, []);

    useEffect(() => {
        getAnalytics();
    }, [hourlyAnalytics]);

    const handleAnalyticsMetric = event => {
        setAnalyticsMetric(event.target.value)
    }

    const handleAnalyticsTimeFrame = event => {
        setAnalyticsTimeFrame(event.target.value)
    }

    const createAnalyticsGraph = () => {

        const days = [];
        const data = [];
        const today = DateTime.local().startOf('day').plus({days: 1});
        let day = DateTime.local();

        switch (analyticsTimeFrame) {
            case 'Last 30 Days':
                day = day.minus({days: 30});
                break;
            case 'Last 60 Days':
                day = day.minus({days: 60});
                break;
            case 'Last 90 Days':
                day = day.minus({days: 90});
                break;
            case 'Last 180 Days':
                day = day.minus({days: 180});
                break;
            case 'Current Year':
                day = day.startOf('year');
                break;
            case 'Past 5 Years':
                day = day.minus({years: 5});
                break;
        }

        while (day <= today) {
            const date = day.toLocaleString(DateTime.DATE_MED);
            const key = day.toFormat('yyyy-MM-dd');

            days.push(date);
            data.push(analytics[key] ? analytics[key][analyticsMetric] : 0);
            day = day.plus({days: 1});
        }

        let gradient = null;

        if (ctx) {
            gradient = ctx.createLinearGradient(0, 400, 0, 100);
            gradient.addColorStop(1, theme.palette.secondary.main);
            gradient.addColorStop(0, fade(theme.palette.secondary.main, 0.2));
        }

        analyticsChart.data = {
            labels: days,
            datasets: [{
                data: data,
                borderWidth: 1,
                backgroundColor: gradient
            }]
        }

        analyticsChart.update();
    }

    useEffect(() => {

        if (analyticsChart) createAnalyticsGraph();

    }, [analytics, analyticsTimeFrame, analyticsMetric])

    const getNewAnalytics = () => {
        setAnalyticsLoading(true);

        api.get('/admin/analytics/new').then(res => setAnalytics(res.analytics)).then(() => setAnalyticsLoading(false));
    }

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <Typography variant="h1" className={classes.pageTitle}>Admin Dashboard</Typography>
                <div className={classes.analyticsContainer}>
                    <div className={classes.analyticsToolbar}>
                        <Typography variant="h5">Club Overview</Typography>
                        <div>
                            <TextField
                                id="select-metric"
                                select
                                label="Select A Metric"
                                value={analyticsMetric}
                                onChange={handleAnalyticsMetric}
                                helperText="Select the metric you wish to view"
                                variant="outlined"
                                className={classes.analyticsSelect}
                                >
                                {metrics.map(metric => (
                                    <MenuItem key={metric.value} value={metric.value}>
                                        {metric.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                id="select-time-frame"
                                select
                                label="Select A Timeframe"
                                value={analyticsTimeFrame}
                                onChange={handleAnalyticsTimeFrame}
                                helperText="Select the timeframe you wish to view"
                                variant="outlined"
                                className={classes.analyticsSelect}
                                >
                                {timeframes.map((t, i) => (
                                    <MenuItem key={i} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Tooltip title="Refresh Analytics">
                                <IconButton
                                    aria-label="Refresh Analytics"
                                    onClick={getNewAnalytics}
                                    className={classes.analyticsSelect}
                                    >
                                    <RefreshIcon
                                        className={analyticsLoading ? classes.rotate : classes.noRotate} fontSize="small"
                                    />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </div>
                    <div className={classes.canvasContainer}>
                        <canvas id="analyticsGraph"></canvas>
                    </div>
                </div>
                { (app.user.access.indexOf('manage_access_requests') > -1) &&
                    <div className={classes.accessRequestsContainer}>
                        <Typography variant="h5">Access Requests</Typography>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>Users who have requested access to the club.</Typography>
                        <AccessRequests limit/>
                    </div>
                }
                { 
                // (app.user.access.indexOf('manage_resignation_requests') > -1) &&
                    <div className={classes.accessRequestsContainer}>
                        <Typography variant="h5">Resignation Requests</Typography>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>Users who have requested to resign from this club.</Typography>
                        <ResignationRequests limit/>
                    </div>
                }
            </div>
            <div className={classes.nav}>
                <div className={classes.navContainer}>
                    <Typography variant="h5" gutterBottom>Tasks</Typography>
                    <Tasks/>
                </div>
            </div>
        </div>
    );
}
